///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { GoogleMapsConfig } from '../services/google-maps-config.service';
import { GtthHistoryService } from './logical-topology/engine/history-service';
import { GtthRemoveMultipleEntitiesCommand } from './logical-topology/engine/commands/remove-multiple-entities-command';
import { GtthPreloaderModalEvent } from '../shared/preloader-modal/preloader-modal-event';
import { GtthPosition } from './logical-topology/entities/position';
import { GtthCreateTerminalCommand } from './logical-topology/engine/commands/node/create-terminal-command';
import { GtthLogSenderService } from '../services/logger/backend-logging/logs-sender.service';
import { ActionsToolbarModeEnum, GtthStepGroupEnum, MapTypeEnum, ProjectDTO } from '../generated/shared-types.model';
import { GtthEventManager } from '../services/event-manager.service';
import { GtthNotificationsService } from './logical-topology/engine/notifications-service';
import { ProjectService } from '../services/project/project.service';
import { GtthInfoWindowsService } from './logical-topology/map-entities-info/info-windows-service';
import { GtthProjectStateService } from './logical-topology/engine/project-state.service';
import { Principal } from '../shared/auth/principal.service';
import { GtthTunnelsService } from './project-windows/reports/tunnels/tunnels.service';
import { ProjectInteractiveWindowsPopupService } from './project-windows/project-interactive-windows-popup.service';
import { ProjectMigrationStateService } from './project-settings-panel/project-migration-state.service';
import { ProjectRoutingStateService } from '../projects/project-routing-state.service';
import { ActionsToolbarSearchService } from './actions-toolbar/actions-toolbar-search.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'winde-project-view-panel',
    templateUrl: './project-view.component.html',
    styleUrls: ['./project-view.scss'],
    providers: [GtthTunnelsService, ActionsToolbarSearchService, ProjectMigrationStateService],
    standalone: false
})
export class GtthProjectViewComponent implements OnInit, OnDestroy {
    @ViewChild('mapContainer', { static: true }) mapContainer: ElementRef;

    public isMapConfigLoaded = false;
    public showProjectSettingsPanel = false;
    private projectOpeningInProgress = false;
    private mapClickListener: any;
    private rightClickListener: any;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public historyService: GtthHistoryService,
        private readonly googleMapsConfig: GoogleMapsConfig,
        private readonly activatedRoute: ActivatedRoute,
        private readonly logSender: GtthLogSenderService,
        private readonly eventManager: GtthEventManager,
        private readonly notificationService: GtthNotificationsService,
        private readonly projectService: ProjectService,
        private readonly ngZone: NgZone,
        private readonly infoWindowsService: GtthInfoWindowsService,
        public projectStateService: GtthProjectStateService,
        private readonly principal: Principal,
        private readonly viewContainerRef: ViewContainerRef,
        private readonly projectInteractiveWindowsService: ProjectInteractiveWindowsPopupService,
        private readonly projectRecoveryService: ProjectMigrationStateService,
        private readonly projectRoutingStateService: ProjectRoutingStateService
    ) {
        this.projectInteractiveWindowsService.setProjectViewPanelContainerRef(viewContainerRef);
    }

    ngOnInit() {
        this.notificationService.clearAllMessages();
        this.historyService.confirmationService.clearAllMessages();

        this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params) => {
            if (this.projectStateService.projectIsOpened()) {
                await this.destroyProjectView();
            }
            await this.loadProjectInfo(parseFloat(params.id));
        });
    }

    public isProjectOpeningInProgress(): boolean {
        return this.projectOpeningInProgress;
    }

    async loadProjectInfo(projectId: number) {
        this.isMapConfigLoaded = false;
        this.projectOpeningInProgress = true;

        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        try {
            this.mapContainer.nativeElement.append(this.googleMapsConfig.getMapElement());
            this.historyService.map = await lastValueFrom(this.googleMapsConfig.getMap());
        } catch (error) {
            console.error('googleMapsConfig.load error: ', error);
            this.projectStateService.clearProject();
            this.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
            return;
        }

        const loadedProject: ProjectDTO = await lastValueFrom(this.projectService.getProject(projectId));

        this.projectRoutingStateService.rememberLastOpened(loadedProject);

        try {
            const userDto = await this.principal.identity();

            this.isMapConfigLoaded = await this.historyService.openProject(loadedProject, this.logSender, userDto.login);

            if (this.isMapConfigLoaded) {
                this.initListeners();

                this.projectRecoveryService.loadAndHandleProjectMigrationState();

                this.eventManager.broadcast({
                    name: 'togglePreloaderModal',
                    eventObject: GtthPreloaderModalEvent.hide()
                });
            } else {
                this.projectStateService.clearProject();
            }
        } catch (error) {
            console.error(`Open project error ${error}`);
            this.projectStateService.clearProject();
            this.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.logSender.logError('Got error ' + (error ? JSON.stringify(error) : 'unknown'));
        } finally {
            this.projectOpeningInProgress = false;
        }
    }

    initListeners() {
        this.mapClickListener = this.historyService.map.addListener('click', (mouseEvent) => {
            this.ngZone.run(() => {
                if (this.historyService.selectAreaService.overlay) {
                    this.historyService.selectAreaService.destroyOverlay();
                    this.historyService.selectAreaService.areaModeChanged();
                }

                if (this.historyService.hasSearchMarkers()) {
                    this.historyService.clearSearchMarker();
                }

                if (this.historyService.logicalTopology.hasSelectedEntities()) {
                    this.historyService.logicalTopology.clearSelection();
                    return;
                }

                this.historyService.closeInfoWindowAndArea();

                switch (this.historyService.toolbarMode) {
                    case ActionsToolbarModeEnum.TERMINAL:
                        this.terminalToolbarMode(mouseEvent);
                        break;

                    case ActionsToolbarModeEnum.NO_LOS:
                    case ActionsToolbarModeEnum.CONFIRMED_LOS:
                    case ActionsToolbarModeEnum.NOT_CONFIRMED_LOS:
                    case ActionsToolbarModeEnum.LINK:
                    case ActionsToolbarModeEnum.FIBER:
                        this.historyService.logicalTopology.clearSelection();
                        break;
                }
            });
        });

        this.rightClickListener = this.historyService.map.addListener('rightclick', () => {
            this.ngZone.run(() => {
                this.historyService.clearSelectionCloseWindowAndArea();
            });
        });
    }

    public triggerProjectSettingsPanel(isShow: boolean) {
        this.showProjectSettingsPanel = isShow;
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardDownEvent(event: KeyboardEvent) {
        switch (event.key) {
            case 'Escape':
                this.escapeButtonPressed();
                break;

            case 'Delete':
                if (
                    !this.infoWindowsService.isWindowOpen &&
                    this.historyService.logicalTopology.hasSelectedNotFrozenEntities() &&
                    this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES
                ) {
                    const removeMultipleEntitiesCommand = this.historyService.command(GtthRemoveMultipleEntitiesCommand);
                    this.historyService.doCommand(removeMultipleEntitiesCommand);
                }
                break;

            case 'Control':
                this.historyService.isCtrlKeyPressed = true;
                break;

            case 'Enter':
                this.historyService.closeInfoWindowAndArea();
                break;
        }
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Control') {
            this.historyService.isCtrlKeyPressed = false;
        }
    }

    @HostListener('window:beforeunload')
    ngOnDestroy(): Promise<void> {
        return this.destroyProjectView();
    }

    private async destroyProjectView(): Promise<void> {
        const mapTypeId = this.historyService.map.getMapTypeId() === MapTypeEnum.ROAD_MAP ? MapTypeEnum.ROAD_MAP : MapTypeEnum.HYBRID;
        this.historyService.projectUserSessionService.setMapType(mapTypeId);

        this.historyService.projectUserSessionService.applyMapCoords(
            this.historyService.map.getCenter(),
            this.historyService.map.getZoom()
        );
        if (this.historyService.logicalTopology) {
            this.historyService.projectUserSessionService.refreshSelectionAndOpenWindows(this.historyService);
        }
        this.historyService.projectUserSessionService.closeForModifications();

        const streetViewPanorama = this.historyService.map.getStreetView();
        if (streetViewPanorama?.getVisible()) {
            streetViewPanorama.setVisible(false);
        }

        const closedProject = this.projectStateService.getProject();

        if (this.infoWindowsService.isWindowOpen) {
            this.infoWindowsService.forceInfoWindowClose();
        }

        await this.historyService.closeProject();

        this.eventManager.broadcast({ name: 'projectClosed', content: { closedProject } });
        this.isMapConfigLoaded = false;

        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
            this.mapClickListener = undefined;
        }
        if (this.rightClickListener) {
            google.maps.event.removeListener(this.rightClickListener);
            this.rightClickListener = undefined;
        }

        this.historyService.clearSearchMarker();
    }

    private terminalToolbarMode(mouseEvent): void {
        const terminalPosition = GtthPosition.fromEventObject(mouseEvent);
        const addTerminalCommand = this.historyService
            .command(GtthCreateTerminalCommand)
            .createFromPosition(terminalPosition)
            .createForLayer(this.historyService.currentMarkerIndex);
        this.historyService.doCommand(addTerminalCommand);
    }

    private escapeButtonPressed(): void {
        const streetViewPanorama = this.historyService.map.getStreetView();
        if (streetViewPanorama.getVisible()) {
            streetViewPanorama.setVisible(false);
        }

        this.historyService.clearSelectionCloseWindowAndArea();

        setTimeout(() => {
            //delay for remove search markers before closing modal windows
            this.historyService.clearSearchMarker();
        }, 0);
    }
}
